import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Banner, Button, Flex, FormFieldAPI, H2, Table, Tile, Title } from '@procore/core-react';
import { Ban, Warning } from '@procore/core-icons';
import { Popup } from 'semantic-ui-react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { IQueryParams, ISortableHeader } from '../../app/interfaces/ISortableTable';
import { IVendor } from '../../app/interfaces/IVendor';
import { getCompanyVendorsThunk, getProjectVendorsThunk, selectVendors, selectVendorsState } from '../../slices/vendorsSlice';
import { getCompanyTradesThunk, selectTrades, selectTradesStatus } from '../../slices/tradesSlice';
import { getCompanyCostCodesThunk, selectCostCodes, selectCostCodesStatus } from '../../slices/costCodesSlice';
import { StarSize } from '../stars/VendorRatingStars';
import SortableTable from '../tables/SortableTable';
import { ProcoreEmptyCell, ProcoreGenericCell, ProcoreLinkErpCell, ProcoreTextCell } from '../tables/ProcoreTableCells';
import TruncatedTextCell from '../tables/TruncatedTextCell';
import { ProcoreVendorRatingStars } from '../stars/ProcoreVendorRatingStars';
import styled from 'styled-components';
import { StyledProcoreTile, StyledTileActions } from '../CommonStyledComponents';
import PageHeader from '../commonComponents/PageHeader';
import { getQuerySettingsThunk, selectQuerySettingsState } from '../../slices/querySettingsSlice';

const vendorIsInactive = (vendor: IVendor) => {
    const candidateValues = [
        (vendor as any)?.active,
        (vendor as any)?.inactive,
        (vendor as any)?.is_active,
        (vendor as any)?.isActive,
    ];

    for (const value of candidateValues) {
        if (typeof value === 'boolean') {
            return value === false;
        }
        if (typeof value === 'number') {
            if (value === 0) return true;
            if (value === 1) return false;
        }
        if (typeof value === 'string') {
            const normalized = value.trim().toLowerCase();
            if (['false', '0', 'inactive', 'in-active', 'no', 'disabled'].includes(normalized)) {
                return true;
            }
            if (['true', '1', 'active', 'yes', 'enabled'].includes(normalized)) {
                return false;
            }
        }
    }

    const status = (vendor as any)?.status || (vendor as any)?.state;
    if (typeof status === 'string' && status.trim().length) {
        return status.trim().toLowerCase().includes('inactive');
    }

    return false;
};

const Vendors: FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const params = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const companyId: number = parseInt(params.companyId!);
    const maybeProjectId: number | null = params.projectId ? parseInt(params.projectId!) : null;
    const { queryState } = useAppSelector(selectVendorsState);
    const { data: querySettings, status: querySettingsStatus } = useAppSelector(selectQuerySettingsState);
    const trades = useAppSelector(selectTrades);
    const tradesStatus = useAppSelector(selectTradesStatus);
    const costCodes = useAppSelector(selectCostCodes);
    const costCodesStatus = useAppSelector(selectCostCodesStatus);
    const [displayAuthMessage, setDisplayAuthMessage] = useState<boolean>(false);
    const hydratedQuerySettingsRef = useRef(false);

    // This file supports the company vendors page AND the project vendors page.
    // If we have a project id as a param we're on the project page.
    // Which page we're on will dictate which query we use, etc.
    const vendorsPage: 'company' | 'project' = maybeProjectId && !isNaN(maybeProjectId) ? 'project' : 'company';

    // Load trades and cost codes for filtering (only for company vendors page)
    useEffect(() => {
        if (vendorsPage === 'company' && tradesStatus === 'idle') {
            dispatch(getCompanyTradesThunk(companyId));
        }
        if (vendorsPage === 'company' && costCodesStatus === 'idle') {
            console.log('🔍 Loading cost codes for company:', companyId);
            dispatch(getCompanyCostCodesThunk(companyId));
        }
    }, [dispatch, companyId, vendorsPage, tradesStatus, costCodesStatus]);

    useEffect(() => {
        if (!querySettings && querySettingsStatus === 'idle') {
            dispatch(getQuerySettingsThunk(companyId));
        }
    }, [dispatch, companyId, querySettings, querySettingsStatus]);

    useEffect(() => {
        hydratedQuerySettingsRef.current = false;
    }, [companyId]);

    useEffect(() => {
        if (!querySettings || hydratedQuerySettingsRef.current) {
            return;
        }

        hydratedQuerySettingsRef.current = true;

        const includeInactiveParam = searchParams.get('include_inactive');
        const desiredValue = querySettings.showInactiveVendors ? 'true' : null;
        const alreadyMatches = (desiredValue === null && includeInactiveParam === null) ||
            (desiredValue !== null && includeInactiveParam === desiredValue);

        if (alreadyMatches) {
            return;
        }

        const next = new URLSearchParams(searchParams);
        next.delete('page');

        if (desiredValue) {
            next.set('include_inactive', desiredValue);
        } else {
            next.delete('include_inactive');
        }

        setSearchParams(next, { replace: true });
    }, [querySettings, searchParams, setSearchParams]);

    // Debug logging for cost codes
    useEffect(() => {
        console.log('🔍 Cost codes debug:', {
            vendorsPage,
            costCodesStatus,
            costCodesCount: costCodes?.length || 0,
            costCodes: costCodes,
            tradesCount: trades?.length || 0
        });
    }, [vendorsPage, costCodesStatus, costCodes, trades]);

    const rateVendor = (vendor: IVendor) => {
        const ratingUrl = maybeProjectId ?
            `/companies/${companyId}/projects/${maybeProjectId}/vendors/${vendor.id}/rating` :
            `/companies/${companyId}/vendors/${vendor.id}/rating`;

        navigate(ratingUrl);
    };

    const columnHeaders: ISortableHeader[] = [
        { value: 'name', key: 'name', text: 'Vendor', sortable: true },
        { value: 'city', key: 'city', text: 'City', sortable: true },
        { value: 'state_code', key: 'state_code', text: 'State', sortable: true },
        { value: 'trades', key: 'trades', text: 'Trade(s)', sortable: false },
        { value: 'cost_codes', key: 'cost_codes', text: 'Cost Code(s)', sortable: false },
        { value: 'average_rating', key: 'average_rating', text: 'Rating', sortable: true },
        { value: 'total_ratings', key: 'total_ratings', text: '# Ratings', sortable: true },
        { value: 'ratings', key: 'ratings', text: 'Rate', sortable: false }
    ];

    const tradeFilter = searchParams.get('trade');
    const costCodeFilter = searchParams.get('cost_code');
    const includeInactiveFilter = searchParams.get('include_inactive') === 'true';

    const queryVendors = useCallback((queryParams?: IQueryParams) => {
        // Add trade filter from URL parameters
        const paramsWithTrade = tradeFilter && tradeFilter !== 'all' ? { ...queryParams, trade: tradeFilter } : queryParams;
        // Add cost code filter from URL parameters
        const paramsWithFilters = costCodeFilter && costCodeFilter !== 'all' ? { ...paramsWithTrade, cost_code: costCodeFilter } : paramsWithTrade;
        const paramsWithInactive = includeInactiveFilter ? { ...paramsWithFilters, include_inactive: true } : paramsWithFilters;

        console.log('🔍 Querying vendors with params:', paramsWithInactive);

        if (vendorsPage === 'project') {
            dispatch(getProjectVendorsThunk({ companyId: companyId, projectId: maybeProjectId!, queryParams: paramsWithInactive }));
        } else if (vendorsPage === 'company') {
            dispatch(getCompanyVendorsThunk({ companyId: companyId, queryParams: paramsWithInactive }));
        }
    }, [dispatch, companyId, maybeProjectId, vendorsPage, tradeFilter, costCodeFilter, includeInactiveFilter]);

    const renderCard = (dataItem: any) => {
        const v = dataItem as IVendor;
        const isInactive = vendorIsInactive(v);
        const maybeInactiveBadge = isInactive ? (
            <Popup
                content="Vendor is inactive"
                position="top center"
                trigger={
                    <InactiveVendorIcon role='img' aria-label='Vendor is inactive'>
                        <Ban size='sm' />
                    </InactiveVendorIcon>
                }
            />
        ) : undefined;
        const fakeField = {
            input: {
              value: v.average_rating
            },
            meta: {
              disabled: true
            },
            helpers: { }
          } as FormFieldAPI<number>;

        return (
            <StyledProcoreTile orientation='portrait' key={v.id}>
                <Tile.Content>
                    <Title>
                        <Title.Text>
                            <StyledNameRow>
                                <H2>
                                    <Link to={`/companies/${companyId}/vendors/${v.id}`} >
                                        {v.name}
                                    </Link>
                                </H2>
                                {maybeInactiveBadge}
                            </StyledNameRow>
                        </Title.Text>
                        { 
                            (v.city && v.state_code) &&
                                <Title.Subtext>{v.city}, {v.state_code}</Title.Subtext>
                        }
                        {
                            v.trades && v.trades.length > 0 &&
                                <Title.Subtext>Trades: {v.trades.map(t => t.name).join(', ')}</Title.Subtext>
                        }
                        {
                            v.cost_codes && v.cost_codes.length > 0 &&
                                <Title.Subtext>
                                    Cost Codes: {
                                        v.cost_codes.length > 3 
                                            ? `${v.cost_codes.slice(0, 3).map(cc => cc.code).join(', ')} (+${v.cost_codes.length - 3} more)`
                                            : v.cost_codes.map(cc => cc.code).join(', ')
                                    }
                                </Title.Subtext>
                        }
                    </Title>


                    <Flex>
                        <ProcoreVendorRatingStars
                            size={StarSize.Medium}
                            field={fakeField}
                        />

                        {
                            v.total_ratings > 0 &&
                            <div>{v.total_ratings} rating(s)</div>
                        }
                    </Flex>

                    </Tile.Content>
                    <StyledTileActions>
                        <Button block onClick={() => rateVendor(v)} >
                            Rate this Vendor
                        </Button>
                    </StyledTileActions>
            </StyledProcoreTile>
        )
    };

    const renderVendorRow = (dataItem: any) => {
        const v = dataItem as IVendor;
        const isInactive = vendorIsInactive(v);
        const inactiveBadge = isInactive ? (
            <Popup
                content="Vendor is inactive"
                position="top center"
                trigger={
                    <InactiveVendorIcon role='img' aria-label='Vendor is inactive'>
                        <Ban size='sm' />
                    </InactiveVendorIcon>
                }
            />
        ) : undefined;
        const fakeField = {
            input: {
              value: v.average_rating
            },
            meta: {
              disabled: true
            },
            helpers: { }
          } as FormFieldAPI<number>;

        return (
            <Table.BodyRow key={v.id}>
                <ProcoreLinkErpCell 
                    to={`/companies/${companyId}/vendors/${v.id}`}
                    cta={v.name}
                    inactive={isInactive}
                    inactiveIcon={inactiveBadge}
                    synced_to_erp={v.synced_to_erp}
                />
                <ProcoreTextCell text={v.city} />
                <ProcoreTextCell text={v.state_code} />
                <ProcoreTextCell text={v.trades && v.trades.length > 0 ? v.trades.map(t => t.name).join(', ') : 'None'} />
                <TruncatedTextCell 
                    text={v.cost_codes && v.cost_codes.length > 0 ? v.cost_codes.map(cc => `${cc.full_code} ${cc.name}`).join(', ') : 'None'}
                    maxWidth="150px"
                    maxLength={30}
                />

                {
                    v.average_rating ?
                        <ProcoreGenericCell>
                            <ProcoreVendorRatingStars
                                displayNumericalValue={false}
                                size={StarSize.Medium}
                                field={fakeField}
                            />
                        </ProcoreGenericCell>
                        :
                        <ProcoreEmptyCell />
                }

                {
                    v.total_ratings > 0 ?
                        <ProcoreTextCell text={v.total_ratings.toString()} /> :
                        <ProcoreEmptyCell />
                }

                <ProcoreGenericCell>
                    <Button size='sm' onClick={() => rateVendor(v)} >
                        Rate this Vendor
                    </Button>
                </ProcoreGenericCell>
            </Table.BodyRow>
        );
    };

    return (
        <div id='vendors'>
            <PageHeader title="Vendors" />
            {
                displayAuthMessage &&
                <StyledBanner variant='action'>
                    <Banner.Icon icon={<Warning size='lg' />} />
                    <Banner.Content>
                        <Banner.Title>
                            Add the Sub Ratings Service for your company.
                        </Banner.Title>
                        <Banner.Body>
                            It looks like you don't have access to this information yet.
                            In order to gain access to all your company's vendors, add the Sub Ratings Service on your company's Procore App Management page.
                        </Banner.Body>
                    </Banner.Content>
                    <Banner.Action>
                        <Button onClick={() => navigate('/help')}>Additional Info</Button>
                    </Banner.Action>
                    <Banner.Dismiss onClick={() => setDisplayAuthMessage(false)} />
                </StyledBanner>
                
            }
            
            <SortableTable
                dataSelector={selectVendors}
                headers={columnHeaders}
                renderDataCard={renderCard}
                renderDataRow={renderVendorRow}
                queryState={queryState}
                queryData={queryVendors}
                exportable={true}
                trades={vendorsPage === 'company' ? trades : undefined}
                costCodes={vendorsPage === 'company' ? costCodes : undefined}
            />
        </div>
    );
};

const StyledBanner = styled(Banner)`
    margin-bottom: 16px;
`;

const StyledNameRow = styled('div')`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const InactiveVendorIcon = styled('span')`
    display: inline-flex;
    align-items: center;
    color: ${props => props.theme.colors?.warning || '#d97706'};
`;

export default Vendors;
